import { imageUrl } from "../../services/assets/assets.ts";

/**
 * Lightweight celebration effects drawn on one full-screen canvas above the
 * React UI (confetti, sparkles, flying coins). The loop only runs while
 * particles are alive; counts shrink when the player prefers less motion.
 */
type Kind = "confetti" | "sparkle" | "coin" | "glow";

interface Particle {
  kind: Kind;
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  rot: number;
  vr: number;
  color: string;
  /** Flying coin: quadratic path from (x0,y0) via (cx,cy) to (tx,ty). */
  path?: { x0: number; y0: number; cx: number; cy: number; tx: number; ty: number; delay: number; onArrive?: () => void };
  wobble: number;
}

const CONFETTI_COLORS = ["#ffd84a", "#ff9fc4", "#b98cff", "#7ee6b5", "#8fd3ff", "#ffb38a", "#ffffff"];
const SPARKLE_COLORS = ["#fff6c2", "#ffe27a", "#ffffff", "#ffd1f0"];

let canvas: HTMLCanvasElement | null = null;
let ctx: CanvasRenderingContext2D | null = null;
let particles: Particle[] = [];
let frame = 0;
let last = 0;
let dpr = 1;
const images = new Map<string, HTMLImageElement>();

function reducedMotion(): boolean {
  return typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function scaleCount(n: number): number {
  return reducedMotion() ? Math.ceil(n / 4) : n;
}

function image(id: string): HTMLImageElement | null {
  let img = images.get(id);
  if (!img) {
    const src = imageUrl(id);
    if (!src) return null;
    img = new Image();
    img.src = src;
    images.set(id, img);
  }
  return img.complete && img.naturalWidth > 0 ? img : null;
}

export function attachFxCanvas(element: HTMLCanvasElement | null): void {
  canvas = element;
  ctx = element?.getContext("2d") ?? null;
  resize();
  image("reward.coin");
}

function resize(): void {
  if (!canvas) return;
  dpr = Math.min(2, window.devicePixelRatio || 1);
  canvas.width = Math.round(window.innerWidth * dpr);
  canvas.height = Math.round(window.innerHeight * dpr);
}

if (typeof window !== "undefined") window.addEventListener("resize", resize);

function rand(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

function pick<T>(list: readonly T[]): T {
  return list[Math.floor(Math.random() * list.length)] as T;
}

function start(): void {
  if (frame || !ctx) return;
  last = performance.now();
  frame = requestAnimationFrame(loop);
}

function drawStar(c: CanvasRenderingContext2D, size: number): void {
  // Four-point twinkle.
  c.beginPath();
  for (let i = 0; i < 8; i += 1) {
    const r = i % 2 === 0 ? size : size * 0.28;
    const a = (Math.PI / 4) * i;
    c.lineTo(Math.cos(a) * r, Math.sin(a) * r);
  }
  c.closePath();
  c.fill();
}

function loop(now: number): void {
  frame = 0;
  if (!ctx || !canvas) return;
  const dt = Math.min(0.05, (now - last) / 1000);
  last = now;
  const c = ctx;
  c.setTransform(1, 0, 0, 1, 0, 0);
  c.clearRect(0, 0, canvas.width, canvas.height);
  c.setTransform(dpr, 0, 0, dpr, 0, 0);
  const coin = image("reward.coin");

  particles = particles.filter((p) => {
    if (p.path && p.path.delay > 0) {
      p.path.delay -= dt;
      return true;
    }
    p.life += dt;
    const t = p.life / p.maxLife;
    if (t >= 1) {
      p.path?.onArrive?.();
      return false;
    }
    c.save();
    switch (p.kind) {
      case "confetti": {
        p.vy += 520 * dt;
        p.vx *= 1 - 1.2 * dt;
        p.vy *= 1 - 0.9 * dt;
        p.x += (p.vx + Math.sin(p.life * 6 + p.wobble) * 30) * dt;
        p.y += p.vy * dt;
        p.rot += p.vr * dt;
        c.globalAlpha = Math.min(1, (1 - t) * 3);
        c.translate(p.x, p.y);
        c.rotate(p.rot);
        c.scale(Math.cos(p.life * 9 + p.wobble), 1);
        c.fillStyle = p.color;
        c.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
        break;
      }
      case "sparkle": {
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.vx *= 1 - 2.5 * dt;
        p.vy *= 1 - 2.5 * dt;
        p.rot += p.vr * dt;
        const pulse = Math.sin(t * Math.PI);
        c.globalAlpha = pulse;
        c.translate(p.x, p.y);
        c.rotate(p.rot);
        c.shadowColor = "#fff2a8";
        c.shadowBlur = 10;
        c.fillStyle = p.color;
        drawStar(c, p.size * (0.6 + pulse * 0.5));
        break;
      }
      case "glow": {
        const r = p.size * (0.4 + t * 1.2);
        const gradient = c.createRadialGradient(p.x, p.y, 0, p.x, p.y, r);
        gradient.addColorStop(0, p.color);
        gradient.addColorStop(1, "rgba(255,255,255,0)");
        c.globalAlpha = (1 - t) * 0.9;
        c.fillStyle = gradient;
        c.beginPath();
        c.arc(p.x, p.y, r, 0, Math.PI * 2);
        c.fill();
        break;
      }
      case "coin": {
        const path = p.path;
        if (!path) break;
        const e = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
        const u = 1 - e;
        p.x = u * u * path.x0 + 2 * u * e * path.cx + e * e * path.tx;
        p.y = u * u * path.y0 + 2 * u * e * path.cy + e * e * path.ty;
        const size = p.size * (1 - e * 0.35);
        c.translate(p.x, p.y);
        c.scale(Math.abs(Math.cos(p.life * 8 + p.wobble)) * 0.6 + 0.4, 1);
        if (coin) c.drawImage(coin, -size / 2, -size / 2, size, size);
        else {
          c.fillStyle = "#ffcf3f";
          c.beginPath();
          c.arc(0, 0, size / 2, 0, Math.PI * 2);
          c.fill();
        }
        break;
      }
    }
    c.restore();
    return true;
  });

  if (particles.length > 0) frame = requestAnimationFrame(loop);
  else c.clearRect(0, 0, canvas.width, canvas.height);
}

function base(kind: Kind, x: number, y: number): Particle {
  return { kind, x, y, vx: 0, vy: 0, life: 0, maxLife: 1, size: 8, rot: 0, vr: 0, color: "#fff", wobble: Math.random() * 10 };
}

export const fx = {
  /** Confetti from the top edge (rain) or a point (burst). */
  confetti(options: { x?: number; y?: number; count?: number; spread?: number; power?: number } = {}): void {
    const w = window.innerWidth;
    const count = scaleCount(options.count ?? 90);
    for (let i = 0; i < count; i += 1) {
      const burst = options.x !== undefined && options.y !== undefined;
      const p = base("confetti", burst ? (options.x ?? 0) : rand(0, w), burst ? (options.y ?? 0) : rand(-40, -10));
      // spread 1 = the upper half circle, 2 = all directions.
      const angle = burst ? -Math.PI / 2 + rand(-0.5, 0.5) * Math.PI * (options.spread ?? 1) : Math.PI / 2;
      const power = burst ? rand(260, 620) * (options.power ?? 1) : rand(40, 160);
      p.vx = Math.cos(angle) * power;
      p.vy = Math.sin(angle) * power;
      p.size = rand(7, 13);
      p.vr = rand(-8, 8);
      p.maxLife = rand(1.8, 3.2);
      p.color = pick(CONFETTI_COLORS);
      particles.push(p);
    }
    start();
  },

  /** Twinkles flying out from a point, e.g. a star landing. */
  sparkles(x: number, y: number, count = 10, radius = 160): void {
    for (let i = 0; i < scaleCount(count); i += 1) {
      const p = base("sparkle", x, y);
      const angle = rand(0, Math.PI * 2);
      const speed = rand(radius * 0.5, radius * 1.5);
      p.vx = Math.cos(angle) * speed;
      p.vy = Math.sin(angle) * speed;
      p.size = rand(5, 11);
      p.vr = rand(-3, 3);
      p.maxLife = rand(0.6, 1.1);
      p.color = pick(SPARKLE_COLORS);
      particles.push(p);
    }
    const glow = base("glow", x, y);
    glow.size = radius * 0.9;
    glow.maxLife = 0.6;
    glow.color = "rgba(255, 240, 170, 0.9)";
    particles.push(glow);
    start();
  },

  /** Coins arc from one point to another; `onEach` fires as each lands. */
  coins(from: { x: number; y: number }, to: { x: number; y: number }, count: number, onEach?: () => void): void {
    const n = Math.max(1, Math.min(14, scaleCount(count)));
    for (let i = 0; i < n; i += 1) {
      const p = base("coin", from.x, from.y);
      const side = rand(-1, 1);
      p.size = rand(26, 34);
      p.maxLife = rand(0.65, 0.9);
      p.path = {
        x0: from.x + rand(-20, 20),
        y0: from.y + rand(-10, 10),
        cx: (from.x + to.x) / 2 + side * 120,
        cy: Math.min(from.y, to.y) - rand(60, 160),
        tx: to.x,
        ty: to.y,
        delay: i * 0.06,
        ...(onEach ? { onArrive: onEach } : {}),
      };
      particles.push(p);
    }
    start();
  },

  /** A few bursts around a point (perfect clears, chapter finales). */
  fireworks(x: number, y: number, bursts = 3): void {
    for (let b = 0; b < bursts; b += 1) {
      setTimeout(() => {
        const bx = x + rand(-120, 120);
        const by = y + rand(-90, 60);
        fx.sparkles(bx, by, 16, 200);
        fx.confetti({ x: bx, y: by, count: 24, power: 0.8, spread: 2 });
      }, b * 260);
    }
  },

  clear(): void {
    particles = [];
  },
};

/** Centre of an element in viewport coordinates. */
export function centerOf(element: Element | null): { x: number; y: number } | null {
  if (!element) return null;
  const rect = element.getBoundingClientRect();
  return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
}
